// Roblox Terrain（高さマップ）の描画。
//
// TerrainDescriptor（4 studs セルの高さ + 素材グリッド）から、頂点カラー付きの
// 連続曲面メッシュ（島・山・海底）と、波アニメーション付きの半透明水面を作る。
// 実機の smooth terrain の見た目（滑らかな起伏と素材の塗り分け）へ寄せるのが目的。
//
// 三角形の構築は terrainGeometry（物理の trimesh コライダーと共有）に任せ、
// ここでは頂点カラー（素材の塗り分け）とマテリアル・水面シェーダだけを担う。
// 素材色は頂点カラーで持たせるため、素材境界は三角形内で自然にグラデーションする
// （実機のボクセル境界のなじみ方に近い）。
import * as THREE from 'three'

import { buildTerrainGeometry } from '../terrainGeometry'
import {
  buildTerrainIsosurface,
  TerrainIsosurfaceData,
} from '../terrainIsosurface'
import {
  SceneObjectDescriptor,
  TERRAIN_HOLE_HEIGHT,
  TerrainDescriptor,
} from '../types'
import { createMaterialTexture, MATERIAL_TILE_STUDS } from './materialTextures'

export interface TerrainView {
  readonly group: THREE.Group
  /** 水面の波アニメーションを進める（毎フレーム呼ぶ）。 */
  update(dt: number): void
  dispose(): void
}

/** 地表メッシュの質感（草・岩などの共通値。色は頂点カラーで塗り分ける）。 */
const GROUND_ROUGHNESS = 0.95

/** 水面の見た目。実機の Water（明るい青 + 白い泡のきらめき）に合わせる。 */
const WATER_COLOR = 0x2f9cd0
const WATER_OPACITY = 0.8
/** 低いほど波の法線が太陽光のハイライトを拾い、うねりが目に見える。 */
const WATER_ROUGHNESS = 0.07
/** 泡・きらめき模様のタイル周期（studs）と流れる速さ（タイル/秒）。 */
const WATER_PATTERN_TILE_STUDS = 28
const WATER_PATTERN_SCROLL_U = 0.012
const WATER_PATTERN_SCROLL_V = 0.008
/** 波の振幅（studs）と空間周期（studs）。遠目にもうねりが分かる強さにする。 */
const WAVE_AMPLITUDE = 0.7
const WAVE_LENGTH_1 = 26
const WAVE_LENGTH_2 = 13
const WAVE_SPEED_1 = 5.5
const WAVE_SPEED_2 = 3.2
/** 水面メッシュの分割数（波の頂点変位用）。1 枚の矩形なのでこの程度で十分滑らか。 */
const WATER_SEGMENTS = 96

/** 地表の曲面メッシュ（頂点カラー）を作る。三角形が張れなければ null。 */
const buildGroundMesh = (
  terrain: TerrainDescriptor,
  isosurface: TerrainIsosurfaceData | null,
): { mesh: THREE.Mesh; patternTexture: THREE.CanvasTexture | null } | null => {
  // ボクセルがあれば等値面（オーバーハング・洞窟まで再現）、無ければ
  // 旧形式の高さマップで描く。物理（physicsController）も同じ分岐で同じ
  // ジオメトリを使い、見た目と当たり判定を一致させる。
  const geometryData = isosurface ?? buildTerrainGeometry(terrain)
  if (!geometryData) {
    return null
  }

  // 頂点カラー: 素材色を頂点へ塗る。等値面は頂点ごとの素材インデックス、
  // 高さマップはセル中心の頂点（vertexIndex = cellIndex）の素材グリッドから引く。
  // palette の CSS 色 → リニア RGB（ColorManagement 有効時は Color が変換する）。
  const { materials, palette } = terrain
  const paletteColors = palette.map((entry) => new THREE.Color(entry.color))
  const fallbackColor = new THREE.Color('#665C3B')
  const vertexMaterialOf = (vertex: number): number =>
    isosurface ? isosurface.vertexMaterials[vertex] : materials[vertex]
  const vertexCount = geometryData.positions.length / 3
  const colors = new Float32Array(vertexCount * 3)
  for (let vertex = 0; vertex < vertexCount; vertex += 1) {
    const color = paletteColors[vertexMaterialOf(vertex)] ?? fallbackColor
    const base = vertex * 3
    colors[base] = color.r
    colors[base + 1] = color.g
    colors[base + 2] = color.b
  }

  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(geometryData.positions, 3),
  )
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3))
  geometry.setIndex(new THREE.BufferAttribute(geometryData.indices, 1))
  geometry.computeVertexNormals()

  // 地表の素材模様。草系のセルが実際に使われている地形（自然地形）には
  // Grass の葉模様をワールド XZ の平面投影 UV で全面タイルする。模様は
  // 白〜グレーの濃淡なので、map × 頂点カラー の乗算で草は緑の芝、
  // 岩・砂のセルには影ムラとして乗り、頂点カラーの塗り分けを壊さない。
  // palette に載っているだけで未使用の草は対象にしない（穴でない実セルの
  // materials を見る）。
  const isGrassIndex = (materialIndex: number): boolean => {
    const material = palette[materialIndex]?.material
    return material === 'Grass' || material === 'LeafyGrass'
  }
  const hasGrass =
    isosurface ?
      isosurface.vertexMaterials.some(isGrassIndex)
    : materials.some((materialIndex, cell) => {
        if (terrain.heights[cell] <= TERRAIN_HOLE_HEIGHT) {
          return false
        }
        return isGrassIndex(materialIndex)
      })
  const patternTexture = hasGrass ? createMaterialTexture('Grass') : null
  if (patternTexture) {
    const positions = geometryData.positions
    const uvs = new Float32Array((positions.length / 3) * 2)
    for (let vertex = 0; vertex < positions.length / 3; vertex += 1) {
      uvs[vertex * 2] = positions[vertex * 3] / MATERIAL_TILE_STUDS
      uvs[vertex * 2 + 1] = positions[vertex * 3 + 2] / MATERIAL_TILE_STUDS
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2))
  }

  const material = new THREE.MeshStandardMaterial({
    vertexColors: true,
    map: patternTexture,
    roughness: GROUND_ROUGHNESS,
    metalness: 0,
  })
  const mesh = new THREE.Mesh(geometry, material)
  // 地形は最背面の背景。アバターより先に描いて半透明（遮蔽フェード中の物体や
  // 水面）の合成順序を安定させる。
  mesh.renderOrder = -2
  return { mesh, patternTexture }
}

// --- 草デコレーション（Roblox の Terrain.Decoration 相当） ----------------
// 実機は Grass 素材の表面に高さ 1〜2 studs の草が生え、地面に接して置かれた
// パーツとのわずかな隙間を視覚的に覆う。等値面の Grass 三角形の上に、
// 決定論的な疑似乱数で草タフト（3 枚刃）を散布した InstancedMesh で近似する
// （風揺れアニメーションは省略する意図的差異）。

/** 1 タフトの葉の枚数と寸法（studs）。 */
const GRASS_BLADES_PER_TUFT = 3
const GRASS_TUFT_MIN_HEIGHT = 1.1
const GRASS_TUFT_MAX_HEIGHT = 2.1
const GRASS_BLADE_WIDTH = 0.55

/** タフトの面密度（個 / 平方 studs）と総数の上限（描画負荷の安全弁）。 */
const GRASS_DENSITY_PER_STUD2 = 0.2
const GRASS_MAX_TUFTS = 30000

/** 草を生やす最小の法線 Y（急斜面・崖には生やさない）。 */
const GRASS_MIN_NORMAL_Y = 0.55

/** 障害グリッドのセル一辺（studs）。テレインのボクセルと同じ粒度。 */
const GRASS_BLOCKER_CELL_STUDS = 4

/**
 * 草の突き抜け防止用の障害インデックス。シーンの設置物の AABB を XZ グリッドへ
 * バケット化し、タフト位置の干渉判定を O(セル内件数) にする。
 * 実機の草もパーツを貫通するが、薄い台の上へ草の先端が突き出ると悪目立ち
 * するため、設置物と交差するタフトは生やさない（意図的な差異）。
 */
class GrassBlockerIndex {
  private readonly buckets = new Map<
    string,
    Array<{ minY: number; maxY: number }>
  >()

  constructor(objects: ReadonlyArray<SceneObjectDescriptor>) {
    for (const obj of objects) {
      const minX = obj.position.x - obj.size.x / 2
      const maxX = obj.position.x + obj.size.x / 2
      const minZ = obj.position.z - obj.size.z / 2
      const maxZ = obj.position.z + obj.size.z / 2
      const entry = {
        minY: obj.position.y - obj.size.y / 2,
        maxY: obj.position.y + obj.size.y / 2,
      }
      const cx0 = Math.floor(minX / GRASS_BLOCKER_CELL_STUDS)
      const cx1 = Math.floor(maxX / GRASS_BLOCKER_CELL_STUDS)
      const cz0 = Math.floor(minZ / GRASS_BLOCKER_CELL_STUDS)
      const cz1 = Math.floor(maxZ / GRASS_BLOCKER_CELL_STUDS)
      for (let cx = cx0; cx <= cx1; cx += 1) {
        for (let cz = cz0; cz <= cz1; cz += 1) {
          const key = `${cx},${cz}`
          const bucket = this.buckets.get(key)
          if (bucket) {
            bucket.push(entry)
          } else {
            this.buckets.set(key, [entry])
          }
        }
      }
    }
  }

  /** タフト（根本 y〜先端 y+height）が設置物と交差するか。 */
  blocked(x: number, y: number, z: number, height: number): boolean {
    const key = `${Math.floor(x / GRASS_BLOCKER_CELL_STUDS)},${Math.floor(
      z / GRASS_BLOCKER_CELL_STUDS,
    )}`
    const bucket = this.buckets.get(key)
    if (!bucket) {
      return false
    }
    for (const entry of bucket) {
      if (y + height > entry.minY && y < entry.maxY) {
        return true
      }
    }
    return false
  }
}

/** 決定論的な疑似乱数（mulberry32）。リロードしても同じ草配置になる。 */
const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** 草 1 タフトの単位ジオメトリ（先細りの三角形の葉 × 3 枚、放射状）。 */
const createTuftGeometry = (): THREE.BufferGeometry => {
  const positions: number[] = []
  for (let blade = 0; blade < GRASS_BLADES_PER_TUFT; blade += 1) {
    const yaw = (blade / GRASS_BLADES_PER_TUFT) * Math.PI
    const cos = Math.cos(yaw)
    const sin = Math.sin(yaw)
    const half = GRASS_BLADE_WIDTH / 2
    // 根本 2 点 + 先端 1 点（先端はわずかに横へ流して自然に見せる）。
    positions.push(-half * cos, 0, -half * sin)
    positions.push(half * cos, 0, half * sin)
    positions.push(0.25 * sin, 1, -0.25 * cos)
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  )
  geometry.computeVertexNormals()
  return geometry
}

/**
 * 等値面の Grass 三角形へ草タフトを散布した InstancedMesh を作る。
 * 草が 1 本も生えない（Grass 面が無い）場合は null。
 */
const buildGrassTufts = (
  isosurface: TerrainIsosurfaceData,
  palette: TerrainDescriptor['palette'],
  blockers: GrassBlockerIndex,
): THREE.InstancedMesh | null => {
  const grassIndexSet = new Set<number>()
  palette.forEach((entry, index) => {
    if (entry.material === 'Grass' || entry.material === 'LeafyGrass') {
      grassIndexSet.add(index)
    }
  })
  if (grassIndexSet.size === 0) {
    return null
  }
  const { positions, indices, vertexMaterials } = isosurface
  interface Tuft {
    x: number
    y: number
    z: number
    yaw: number
    height: number
    shade: number
  }
  const tufts: Tuft[] = []
  const random = createRandom(0x517cc1)
  for (
    let i = 0;
    i < indices.length && tufts.length < GRASS_MAX_TUFTS;
    i += 3
  ) {
    const a = indices[i]
    const b = indices[i + 1]
    const c = indices[i + 2]
    if (
      !grassIndexSet.has(vertexMaterials[a]) ||
      !grassIndexSet.has(vertexMaterials[b]) ||
      !grassIndexSet.has(vertexMaterials[c])
    ) {
      continue
    }
    const ax = positions[a * 3]
    const ay = positions[a * 3 + 1]
    const az = positions[a * 3 + 2]
    const abx = positions[b * 3] - ax
    const aby = positions[b * 3 + 1] - ay
    const abz = positions[b * 3 + 2] - az
    const acx = positions[c * 3] - ax
    const acy = positions[c * 3 + 1] - ay
    const acz = positions[c * 3 + 2] - az
    const nx = aby * acz - abz * acy
    const ny = abz * acx - abx * acz
    const nz = abx * acy - aby * acx
    const doubleArea = Math.sqrt(nx * nx + ny * ny + nz * nz)
    if (doubleArea <= 0 || ny / doubleArea < GRASS_MIN_NORMAL_Y) {
      continue
    }
    // 面積に比例した本数（端数は確率で 1 本）。
    const expected = (doubleArea / 2) * GRASS_DENSITY_PER_STUD2
    let count = Math.floor(expected)
    if (random() < expected - count) {
      count += 1
    }
    for (let k = 0; k < count && tufts.length < GRASS_MAX_TUFTS; k += 1) {
      // 三角形内の一様サンプル（重心座標）。
      let u = random()
      let v = random()
      if (u + v > 1) {
        u = 1 - u
        v = 1 - v
      }
      const x = ax + abx * u + acx * v
      const y = ay + aby * u + acy * v
      const z = az + abz * u + acz * v
      const yaw = random() * Math.PI
      const height =
        GRASS_TUFT_MIN_HEIGHT +
        random() * (GRASS_TUFT_MAX_HEIGHT - GRASS_TUFT_MIN_HEIGHT)
      const shade = 0.75 + random() * 0.45
      // 乱数消費を一定にしてから除外する（配置の決定論を保つ）。
      if (blockers.blocked(x, y, z, height)) {
        continue
      }
      tufts.push({ x, y, z, yaw, height, shade })
    }
  }
  if (tufts.length === 0) {
    return null
  }
  const material = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: 1,
    metalness: 0,
    side: THREE.DoubleSide,
  })
  const mesh = new THREE.InstancedMesh(
    createTuftGeometry(),
    material,
    tufts.length,
  )
  const baseColor = new THREE.Color(
    palette.find(
      (entry) => entry.material === 'Grass' || entry.material === 'LeafyGrass',
    )?.color ?? '#7baf50',
  )
  const matrix = new THREE.Matrix4()
  const quaternion = new THREE.Quaternion()
  const up = new THREE.Vector3(0, 1, 0)
  const scale = new THREE.Vector3()
  const position = new THREE.Vector3()
  const color = new THREE.Color()
  tufts.forEach((tuft, index) => {
    quaternion.setFromAxisAngle(up, tuft.yaw)
    position.set(tuft.x, tuft.y - 0.05, tuft.z)
    scale.set(1, tuft.height, 1)
    matrix.compose(position, quaternion, scale)
    mesh.setMatrixAt(index, matrix)
    color.copy(baseColor).multiplyScalar(tuft.shade)
    mesh.setColorAt(index, color)
  })
  mesh.instanceMatrix.needsUpdate = true
  if (mesh.instanceColor) {
    mesh.instanceColor.needsUpdate = true
  }
  // 草原全体が視界に入る使い方が普通で 1 ドローのため、カリングはしない
  // （InstancedMesh は全インスタンスを包む境界の自動計算をしないため）。
  mesh.frustumCulled = false
  mesh.renderOrder = -1
  return mesh
}

/**
 * 水面の泡・きらめき模様（タイル可能な CanvasTexture）を生成する。
 *
 * 実機の Water 上面（明るい水色 + 白い泡の筋が流れる）に寄せるため、
 * 明るい下地にぼかした白い斑と薄暗い揺らぎを散らす。ブラー円はタイル境界を
 * またいで 9 回描き、シームレスにする。
 */
const createWaterPatternTexture = (): THREE.CanvasTexture => {
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (context) {
    // 下地: わずかに濃淡のある明るいグレー（material.color が青を乗せる）。
    context.fillStyle = '#C9D4DA'
    context.fillRect(0, 0, size, size)
    // 決定的な疑似乱数（seed 固定）。毎回同じ模様になりリロードでチラつかない。
    let seed = 11
    const random = (): number => {
      seed = (seed * 16807) % 2147483647
      return seed / 2147483647
    }
    const drawWrapped = (
      x: number,
      y: number,
      radius: number,
      color: string,
    ): void => {
      for (const ox of [-size, 0, size]) {
        for (const oy of [-size, 0, size]) {
          const gradient = context.createRadialGradient(
            x + ox,
            y + oy,
            0,
            x + ox,
            y + oy,
            radius,
          )
          gradient.addColorStop(0, color)
          gradient.addColorStop(1, 'rgba(255,255,255,0)')
          context.fillStyle = gradient
          context.beginPath()
          context.arc(x + ox, y + oy, radius, 0, Math.PI * 2)
          context.fill()
        }
      }
    }
    // 暗めの揺らぎ（深み）を先に、白い泡の斑を後に重ねる。
    for (let i = 0; i < 26; i += 1) {
      drawWrapped(
        random() * size,
        random() * size,
        18 + random() * 30,
        'rgba(120,150,165,0.35)',
      )
    }
    for (let i = 0; i < 34; i += 1) {
      drawWrapped(
        random() * size,
        random() * size,
        6 + random() * 18,
        'rgba(255,255,255,0.5)',
      )
    }
    for (let i = 0; i < 10; i += 1) {
      drawWrapped(
        random() * size,
        random() * size,
        3 + random() * 7,
        'rgba(255,255,255,0.9)',
      )
    }
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  return texture
}

/**
 * 波アニメーション付きの水面メッシュを作る。
 *
 * MeshStandardMaterial の頂点シェーダへ「2 方向の正弦波の和」による変位を
 * 注入し、解析的な法線（偏微分から算出）でハイライトも揺らす。フラグメント側は
 * 標準のままなので、ライティング・トーンマッピングは他のオブジェクトと一致する。
 * 泡・きらめきは流れる模様テクスチャ（map）で表現し、update で offset を進める。
 */
const buildWaterMesh = (
  water: NonNullable<TerrainDescriptor['water']>,
): {
  mesh: THREE.Mesh
  timeUniform: { value: number }
  patternTexture: THREE.CanvasTexture
} => {
  const sizeX = water.x1 - water.x0
  const sizeZ = water.z1 - water.z0
  const geometry = new THREE.PlaneGeometry(
    sizeX,
    sizeZ,
    WATER_SEGMENTS,
    WATER_SEGMENTS,
  )
  // ジオメトリ自体を XZ 平面（法線 +Y）へ焼き込み、シェーダ内の座標を
  // ワールド軸と揃える（メッシュ回転だと position.xz が使えない）。
  geometry.rotateX(-Math.PI / 2)

  const patternTexture = createWaterPatternTexture()
  patternTexture.repeat.set(
    sizeX / WATER_PATTERN_TILE_STUDS,
    sizeZ / WATER_PATTERN_TILE_STUDS,
  )
  const material = new THREE.MeshStandardMaterial({
    color: WATER_COLOR,
    map: patternTexture,
    transparent: true,
    opacity: WATER_OPACITY,
    roughness: WATER_ROUGHNESS,
    metalness: 0,
    // 水中からも水面が見えるように両面描画する（遊泳中の視点対応）。
    side: THREE.DoubleSide,
    // 半透明 + 両面の水面が depth を書くと、水中の物体・他の半透明物との
    // 合成順序が破綻する（水面の裏面が手前面を遮る等）。深度は読むだけにする。
    depthWrite: false,
  })
  const timeUniform = { value: 0 }
  material.onBeforeCompile = (shader) => {
    // three のバージョン更新で置換対象の chunk 名が変わった場合は、波の注入を
    // 丸ごと諦めて標準シェーダのまま描く（部分適用でコンパイルエラーや
    // 法線だけ揺れる等の壊れ方をしない）。
    const anchors = [
      '#include <common>',
      '#include <beginnormal_vertex>',
      '#include <begin_vertex>',
    ]
    if (!anchors.every((anchor) => shader.vertexShader.includes(anchor))) {
      console.error(
        '[preview] 水面シェーダの注入先 chunk が見つかりません。波アニメーションなしで描画します',
      )
      return
    }
    shader.uniforms.uWaveTime = timeUniform
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        [
          '#include <common>',
          'uniform float uWaveTime;',
          // 2 方向の正弦波の和。phase は (位置 + 時間×速度) / 波長。
          'float waveHeight(vec2 p) {',
          `  float w1 = sin((p.x + p.y * 0.6 + uWaveTime * ${WAVE_SPEED_1.toFixed(2)}) * ${(
            (Math.PI * 2) /
            WAVE_LENGTH_1
          ).toFixed(6)});`,
          `  float w2 = sin((p.y - p.x * 0.4 + uWaveTime * ${WAVE_SPEED_2.toFixed(2)}) * ${(
            (Math.PI * 2) /
            WAVE_LENGTH_2
          ).toFixed(6)});`,
          `  return (w1 + w2) * ${(WAVE_AMPLITUDE / 2).toFixed(4)};`,
          '}',
        ].join('\n'),
      )
      .replace(
        '#include <beginnormal_vertex>',
        [
          // 解析的な法線: n = normalize(-dh/dx, 1, -dh/dz)（中央差分で近似）。
          'vec2 wavePos = position.xz;',
          'float waveEps = 0.5;',
          'float waveHx = waveHeight(wavePos + vec2(waveEps, 0.0)) - waveHeight(wavePos - vec2(waveEps, 0.0));',
          'float waveHz = waveHeight(wavePos + vec2(0.0, waveEps)) - waveHeight(wavePos - vec2(0.0, waveEps));',
          'vec3 objectNormal = normalize(vec3(-waveHx / (2.0 * waveEps), 1.0, -waveHz / (2.0 * waveEps)));',
          '#ifdef USE_TANGENT',
          'vec3 objectTangent = vec3( tangent.xyz );',
          '#endif',
        ].join('\n'),
      )
      .replace(
        '#include <begin_vertex>',
        [
          'vec3 transformed = vec3(position);',
          'transformed.y += waveHeight(wavePos);',
        ].join('\n'),
      )
  }
  // onBeforeCompile の変更をキャッシュキーへ反映する（同型マテリアルとの誤共有防止）。
  material.customProgramCacheKey = () => 'terrain-water-waves-v1'

  const mesh = new THREE.Mesh(geometry, material)
  mesh.position.set(
    (water.x0 + water.x1) / 2,
    water.level,
    (water.z0 + water.z1) / 2,
  )
  // 半透明の水面は不透明物の後に描く。
  mesh.renderOrder = -1
  return { mesh, timeUniform, patternTexture }
}

/**
 * TerrainDescriptor から描画用ビューを作る。
 * 有効な地表セルも水面も無いときは null（呼び出し側は地形なしとして扱う）。
 */
export const createTerrainView = (
  terrain: TerrainDescriptor,
  sceneObjects: ReadonlyArray<SceneObjectDescriptor> = [],
): TerrainView | null => {
  const group = new THREE.Group()
  const isosurface = buildTerrainIsosurface(terrain)
  const ground = buildGroundMesh(terrain, isosurface)
  if (ground) {
    group.add(ground.mesh)
  }
  // 草デコレーション（Roblox の Terrain.Decoration 相当）。等値面のときのみ。
  if (isosurface) {
    const grass = buildGrassTufts(
      isosurface,
      terrain.palette,
      new GrassBlockerIndex(sceneObjects),
    )
    if (grass) {
      group.add(grass)
    }
  }
  let waterTimeUniform: { value: number } | null = null
  let waterPatternTexture: THREE.CanvasTexture | null = null
  if (terrain.water) {
    const { mesh, timeUniform, patternTexture } = buildWaterMesh(terrain.water)
    group.add(mesh)
    waterTimeUniform = timeUniform
    waterPatternTexture = patternTexture
  }
  if (group.children.length === 0) {
    waterPatternTexture?.dispose()
    return null
  }

  return {
    group,
    update: (dt: number): void => {
      if (waterTimeUniform) {
        waterTimeUniform.value += dt
      }
      // 泡・きらめき模様をゆっくり流す（頂点の波と別方向だと水らしく見える）。
      if (waterPatternTexture) {
        waterPatternTexture.offset.x += dt * WATER_PATTERN_SCROLL_U
        waterPatternTexture.offset.y -= dt * WATER_PATTERN_SCROLL_V
      }
    },
    dispose: (): void => {
      for (const child of group.children) {
        if (child instanceof THREE.Mesh) {
          child.geometry.dispose()
          const material = child.material
          if (Array.isArray(material)) {
            for (const m of material) {
              m.dispose()
            }
          } else {
            material.dispose()
          }
        }
      }
      // CanvasTexture は material.dispose では解放されないため明示する。
      waterPatternTexture?.dispose()
      ground?.patternTexture?.dispose()
      group.clear()
    },
  }
}
